<template>
  <div
    v-if="shouldBeDisplayed"
    id="partner"
    :class="$style.layout"
  >
    <div
      v-if="!loggedIn || (loggedIn && !hasParticipation)"
      :class="$style.container"
    >
      <h2 :class="$style.title">{{ defaultContent.title }}</h2>
      <div
        v-if="defaultContent.description"
        :class="$style.description"
        v-html="defaultContent.description"
      />
      <template
        v-if="defaultContent.button.label && defaultContent.button.route"
      >
        <ButtonSimple
          v-if="defaultContent.button.route.at(0) === '#'"
          :label="defaultContent.button.label"
          @click="scrollToSection"
        />
        <ButtonSimple
          v-else-if="
            defaultContent.button.route.toLowerCase().startsWith('http')
          "
          tag="a"
          :href="defaultContent.button.route"
          target="_blank"
          :label="defaultContent.button.label"
        />
        <ButtonSimple
          v-else
          :label="defaultContent.button.label"
          :route="defaultContent.button.route"
        />
      </template>
    </div>
    <template v-if="loggedIn">
      <div
        v-if="invitations"
        :class="$style.invitation"
      >
        <h2 :class="$style.title">
          {{ pluralize('Participation', invitations.length) }} en attente de
          validation
        </h2>
        <div
          v-for="(invitation, index) in invitations"
          :key="invitation?.id"
        >
          <hr
            v-if="index === 0"
            :class="[$style.hr, $style['hr--top']]"
          />
          <ContestParticipation
            :rank="0"
            :participation="invitation"
            standalone
          />
          <p
            v-if="invitation.id && errors?.[invitation.id]"
            :class="$style.error"
          >
            <!-- This has never worked -->
            {{ errors[invitation?.id] }}
          </p>
          <template v-else>
            <div :class="$style.buttons">
              <ButtonSimple
                label="Je valide*"
                :disabled="buttonDisabled"
                @click="clickHandler(invitation.id!, true)"
              />
              <ButtonSimple
                label="Je refuse"
                color="danger"
                :disabled="buttonDisabled"
                @click="clickHandler(invitation?.id!, false)"
              />
            </div>
            <p :class="$style.rules">
              *La validation de l'invitation vaut pour acception du
              <NuxtLink
                v-if="recordContest?.rules"
                :to="`/page/${recordContest?.rules.slug}`"
                target="_blank"
                >règlement du jeu</NuxtLink
              >
            </p>
          </template>
          <hr :class="$style.hr" />
        </div>
      </div>
      <div
        v-else-if="pendingInvitation"
        :class="$style.invitation"
      >
        <h2 :class="$style.title">Invitation en attente de validation</h2>
        <hr :class="$style.hr" />
        <ContestParticipation
          :rank="0"
          :participation="pendingInvitation"
          standalone
        />
        <hr :class="$style.hr" />
      </div>
      <div
        v-else-if="currentParticipation"
        :class="[
          $style.invitation,
          { [$style['invitation--solo']]: isSoloContest },
        ]"
      >
        <h2 :class="$style.title">Ma participation</h2>
        <div>
          <hr :class="$style.hr" />
          <ContestParticipation
            personal
            :participation="currentParticipation"
          />
          <hr :class="$style.hr" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  loggedIn?: boolean
}>()

const injectedInvitationApprove = inject(injectedInvitationApproveKey)
const injectedInvitationDisapprove = inject(injectedInvitationDisapproveKey)
const isEndedContest = inject(isEndedContestKey)
const isSoloContest = inject(isSoloContestKey)

const buttonDisabled = ref(false)

const recordContestsStore = useRecordContestsStore()
const { recordContest } = storeToRefs(recordContestsStore)

const defaultContent = computed(() => {
  const rawContest = unref(recordContest)
  return {
    title: rawContest?.team_block_title,
    description: rawContest?.team_block_content,
    button: {
      label: rawContest?.team_block_button_label,
      route: rawContest?.team_block_button_link,
    },
  }
})

const invitations = computed(() => recordContest.value?.invitations)

const pendingInvitation = computed(
  () => recordContest.value?.pending_invitations?.[0],
)

const currentParticipation = computed(() => {
  return recordContest.value?.my_post
})

const hasParticipation = computed(() => {
  return (
    !!invitations.value?.length ||
    !!pendingInvitation.value ||
    !!currentParticipation.value
  )
})

const shouldBeDisplayed = computed(() => {
  if (!props.loggedIn) {
    return !isEndedContest?.value && !!defaultContent.value?.title
  }

  if (isEndedContest?.value) {
    return !!currentParticipation.value
  }
  return !!hasParticipation.value || !!defaultContent.value?.title
})

const errors = ref<Record<number, string>>()

const clickHandler = async (invitationId: number, approve: boolean) => {
  buttonDisabled.value = true
  errors.value = {}

  if (!invitations.value) {
    console.error('invitations.value is null or undefined')
    buttonDisabled.value = false
    return
  }

  const invitationIndex = invitations.value.findIndex(
    (invitation) => invitation.id === invitationId,
  )

  if (invitationIndex === -1) {
    console.error('Invitation not found')
    buttonDisabled.value = false
    return
  }

  const slug = invitations.value?.[invitationIndex].sc_team?.slug
  if (!slug) {
    buttonDisabled.value = false
    return
  }

  if (!injectedInvitationApprove || !injectedInvitationDisapprove) {
    buttonDisabled.value = false

    return console.error('Invitation handling functions are not injected')
  }

  const response = approve
    ? await injectedInvitationApprove(slug)
    : await injectedInvitationDisapprove(slug)

  errors.value = { [invitationId]: response as unknown as string }

  buttonDisabled.value = false
}

const scrollToSection = () => {
  const element = document.querySelector(
    defaultContent.value.button.route as string,
  )

  if (element) {
    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY - 170,
      behavior: 'smooth',
    })
  }
}
</script>

<style lang="scss" module>
.layout {
  position: relative;
  padding: 100px 0;
  color: $white;
  background-image: linear-gradient(45deg, #0d53c5 0%, #032874 90%);

  @media (min-width: 992px) {
    padding: 150px 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    display: inline-block;
    width: 100%;
    height: 20px;
    background-color: #151b27;
    clip-path: polygon(100% 0, 0% 100%, 100% 100%);
    pointer-events: none;

    @media (min-width: 700px) {
      height: 50px;
    }
  }
}

.container {
  padding: 0 25px;
  text-align: center;

  @media (min-width: 700px) {
    width: 100%;
    max-width: 1030px;
    margin: 0 auto;
    padding: 0 80px;
  }
}

.invitation {
  padding: 025px;
  text-align: center;

  @media (min-width: 700px) {
    width: 100%;
    margin: 0 auto;
    padding: 0 80px;
  }

  @media (min-width: 1280px) {
    max-width: 1600px;
  }

  &--solo {
    @media (min-width: 992px) {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
}

.title {
  margin-bottom: 50px;
  font-family: SST;
  font-size: 24px;
  letter-spacing: 0;
  text-transform: none;

  @media (min-width: 700px) {
    font-size: 39px;
  }
}

.description {
  margin-bottom: 50px;

  img {
    max-width: 100% !important;
    height: auto !important;
  }
}

.hr {
  height: 1px;
  background-color: $white;
  border: 0;

  &:last-of-type {
    margin-bottom: 40px;

    @media (min-width: 992px) {
      margin-bottom: 50px;
    }
  }

  &--top {
    margin-top: 50px;
  }
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  margin-bottom: 10px;

  @media (min-width: 992px) {
    flex-direction: row;
    gap: 50px;
    align-items: center;
  }
}

.rules {
  margin-bottom: 50px;
  font-size: 12px;
  text-align: center;

  a {
    color: inherit;
  }
}

.error {
  margin-bottom: 50px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}
</style>
